
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Progress,
  VStack,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getUsefulVars } from '../../handlers/helpers';
import { onLink } from '../../handlers';

const ValuesAssessment = ({props, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({
    Achievement: 0,
    Autonomy: 0,
    Balance: 0,
    Creativity: 0,
    Growth: 0,
    Impact: 0,
    Recognition: 0,
    Security: 0,
    Service: 0,
    Connection: 0
  });
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      text: "Reaching ambitious goals gives me deep satisfaction",
      example: "I love the feeling of checking off big milestones and hitting targets I set for myself",
      value: "Achievement"
    },
    {
      text: "I need the freedom to make my own decisions about how I work",
      example: "Being micromanaged or having to ask permission for everything drains my energy",
      value: "Autonomy"
    },
    {
      text: "Having time for both work and personal life is essential to me",
      example: "I need space for hobbies, family, and downtime - work isn't everything",
      value: "Balance"
    },
    {
      text: "I light up when I get to bring new ideas to life",
      example: "Whether it's problem-solving or making something from scratch, creativity energizes me",
      value: "Creativity"
    },
    {
      text: "Continuously learning and developing new skills matters deeply to me",
      example: "I get restless when I'm not growing or being challenged in new ways",
      value: "Growth"
    },
    {
      text: "I want my work to make a real difference in the world",
      example: "It's important that what I do creates meaningful change, not just busy work",
      value: "Impact"
    },
    {
      text: "Being acknowledged for my contributions is important to me",
      example: "I appreciate when my hard work is noticed and celebrated by others",
      value: "Recognition"
    },
    {
      text: "Stability and predictability help me feel grounded",
      example: "I value knowing I have a reliable income and won't face sudden upheaval",
      value: "Security"
    },
    {
      text: "Helping others and making their lives better fuels me",
      example: "I find the most meaning in work that directly supports or serves people",
      value: "Service"
    },
    {
      text: "Building genuine relationships at work is vital to me",
      example: "I want to actually know and care about the people I work with, not just transact",
      value: "Connection"
    },
    {
      text: "I'm motivated by clear measures of success and winning",
      example: "Tangible results and being the best at what I do drive me forward",
      value: "Achievement"
    },
    {
      text: "Being able to work independently is crucial for me",
      example: "I do my best work when I can chart my own course without constant oversight",
      value: "Autonomy"
    },
    {
      text: "I refuse to sacrifice my wellbeing for work demands",
      example: "Burnout isn't worth it - I need boundaries to stay healthy and happy",
      value: "Balance"
    },
    {
      text: "I get bored quickly if I can't innovate or try new approaches",
      example: "Repetitive work without room for creativity makes me feel stuck",
      value: "Creativity"
    },
    {
      text: "Opportunities to expand my capabilities excite me",
      example: "I actively seek out new challenges that stretch me beyond my comfort zone",
      value: "Growth"
    },
    {
      text: "I need to see how my work contributes to something bigger",
      example: "Understanding the 'why' behind my work and its broader impact keeps me engaged",
      value: "Impact"
    },
    {
      text: "Public appreciation for my work boosts my motivation",
      example: "Awards, praise, or even a genuine 'thank you' mean a lot to me",
      value: "Recognition"
    },
    {
      text: "Financial stability and job security give me peace of mind",
      example: "Knowing I can count on steady income removes anxiety and lets me focus",
      value: "Security"
    },
    {
      text: "Contributing to others' success brings me joy",
      example: "I love being part of someone else's growth or helping them through challenges",
      value: "Service"
    },
    {
      text: "A strong sense of community at work matters to me",
      example: "I want to feel like I belong and that people genuinely care about each other",
      value: "Connection"
    },
  ];

  const valueDescriptions = {
    Achievement: {
      name: "Achievement",
      whatItMeans: "Here's the thing - you're wired for winning. Not in a cutthroat way necessarily, but you genuinely need to see yourself making progress and hitting milestones. When you accomplish something big? That's not just a nice feeling - that's what keeps you going. Stagnation doesn't just bore you; it actually stresses you out. You need goals to chase, wins to celebrate, and proof that you're getting better at what you do.",
      workStyle: "You come alive in environments where there's a scoreboard - whether that's sales numbers, project completions, or career advancement. Give you a clear target and stand back.",
      careers: "Sales, entrepreneurship, competitive sports, executive leadership, performance-based roles"
    },
    Autonomy: {
      name: "Autonomy",
      whatItMeans: "You can't do your best work with someone breathing down your neck. It's not that you're difficult or don't like people - you just need to be trusted to figure things out your own way. Micromanagement doesn't just annoy you; it actually kills your motivation. Tell you what needs to happen and when it's due, then let you work your magic. That's when you shine.",
      workStyle: "You thrive when there's trust instead of oversight. Remote work, flexible hours, or being your own boss? That's your sweet spot.",
      careers: "Freelancing, consulting, research, creative work, remote positions, entrepreneurship"
    },
    Balance: {
      name: "Work-Life Balance",
      whatItMeans: "Look, you work hard - but work isn't your whole identity. You've got people you love, hobbies that recharge you, and a life outside the office that matters just as much as what happens inside it. The whole 'hustle culture' thing? It's not inspiring to you - it's a recipe for burnout. You know that protecting your personal time isn't selfish; it's how you stay healthy and actually good at your job.",
      workStyle: "You need a workplace that respects boundaries. Flexible schedules, reasonable hours, and a culture that doesn't treat exhaustion like a badge of honor.",
      careers: "Companies with strong work-life balance, flexible arrangements, supportive managers who get it"
    },
    Creativity: {
      name: "Creativity & Innovation",
      whatItMeans: "Doing the same thing the same way day after day? That's your personal nightmare. You need room to experiment, question how things have always been done, and try new approaches. It's not that you're difficult - you just can't help but see different ways to do things. When someone says 'that's how we've always done it,' you hear 'nobody's questioned this yet.' Give you a problem and creative freedom, and you'll find solutions nobody else thought of.",
      workStyle: "You flourish when there's space to innovate and permission to try things that might not work. Rigid processes and 'just follow the template' environments drain you fast.",
      careers: "Design, marketing, product development, arts, innovation teams, anywhere 'think different' is actually valued"
    },
    Growth: {
      name: "Learning & Growth",
      whatItMeans: "You've got this thing where you need to feel like you're getting better, learning something new, or being challenged in fresh ways. It's not ambition necessarily - it's more like intellectual curiosity that never turns off. When you've mastered something and there's nothing new to learn? That's when you start looking around for what's next. You're not restless; you're just someone who genuinely loves developing new capabilities.",
      workStyle: "You need opportunities to stretch yourself - whether that's new projects, mentorship, training, or just being thrown into challenges slightly beyond your current skill level.",
      careers: "Fast-growing companies, roles with clear development paths, industries that evolve constantly, anywhere learning is built in"
    },
    Impact: {
      name: "Making an Impact",
      whatItMeans: "You need to know that what you're doing actually matters. Not in a 'it matters to the bottom line' way - in a 'this makes someone's life better' or 'this solves a real problem' way. Busy work, even if it pays well, feels empty to you. You're the person who needs to understand the 'why' behind tasks. When you can see how your work creates meaningful change? That's when you're truly engaged and energized.",
      workStyle: "You thrive in mission-driven work where you can actually see the difference you're making. Purpose isn't just nice to have for you - it's essential.",
      careers: "Nonprofits, social enterprises, healthcare, education, sustainability, advocacy, anything with a clear mission beyond profit"
    },
    Recognition: {
      name: "Recognition & Appreciation",
      whatItMeans: "You're not looking for constant praise or a participation trophy - but you do need to know your work is seen and valued. When you put in real effort and it goes completely unnoticed? That stings more than you usually admit. A genuine 'thank you,' public acknowledgment, or recognition of your contributions isn't about ego - it's about feeling like your hard work matters to someone other than just you.",
      workStyle: "You do well in places that actually celebrate wins, give credit where it's due, and don't let great work disappear into the void unacknowledged.",
      careers: "Roles with built-in recognition (awards, promotions), client-facing work where appreciation is direct, performance-based cultures"
    },
    Security: {
      name: "Stability & Security",
      whatItMeans: "Financial stress and uncertainty make it really hard for you to focus on doing good work. You're not boring or unambitious - you just know yourself well enough to realize you need solid ground under your feet. Steady income, reliable employment, and not worrying if you'll have a job next month? That's not asking for too much - that's what lets you actually relax and bring your best self to work.",
      workStyle: "You thrive in stable environments - established companies, clear structures, predictable income. The 'chaos of a startup' isn't exciting to you; it's anxiety-inducing.",
      careers: "Government, large corporations, tenured positions, regulated industries, anywhere stability is a feature not a bug"
    },
    Service: {
      name: "Service to Others",
      whatItMeans: "At your core, you're someone who finds purpose in being helpful. Whether it's teaching, healing, supporting, or just being there when someone needs you - that's what fills you up. Work that doesn't involve directly helping people feels kind of... pointless? You're not a martyr about it; you just genuinely get your energy from making someone else's day, life, or situation better in some tangible way.",
      workStyle: "You come alive in roles where you can see the direct impact of your help - where people walk away better off because you showed up.",
      careers: "Healthcare, teaching, counseling, social work, customer service, caregiving, any role centered on helping people"
    },
    Connection: {
      name: "Connection & Belonging",
      whatItMeans: "For you, work is personal. You can't just clock in, do tasks with strangers, and clock out feeling good. You need to actually know the people you work with - not in a forced team-building way, but genuinely. You want to care about your colleagues and feel like they care about you too. A job where you feel isolated or where relationships are purely transactional? That's lonely in a way that's hard to explain to people who don't get it.",
      workStyle: "You thrive in collaborative, close-knit environments where people actually like each other and treat work as something done together, not just parallel to each other.",
      careers: "Small teams, mission-driven orgs, anywhere with strong community, roles that involve genuine collaboration not just coordination"
    }
  };

  const handleAnswer = (rating) => {
    const questionValue = questions[currentQuestion].value;

    let points = 0;
    if (rating === 5) points = 2;
    else if (rating === 3) points = 1;
    else if (rating === 1) points = 0;
    else if (rating === -1) points = 0;
    else if (rating === -3) points = 0;

    setScores(prev => ({
      ...prev,
      [questionValue]: prev[questionValue] + points
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const getTopValues = () => {
    const sortedValues = Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .filter(([, score]) => score > 0);
    return sortedValues;
  };

  const handleSaveAndContinue = () => {
    const topValues = getTopValues();


    const uv = getUsefulVars(props);
    const resume = uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex];
    if(resume['new-aptitude'] === undefined) {
      resume['new-aptitude'] = [];
      resume['new-aptitude'].push({});
    }
    //only save label of first value so topValues[0][0] is the label
    uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex]['new-aptitude'][0].values_assessment = topValues[0][0];

    onLink(false, props.formikProps, props.stateProps, 'new-aptitude',0,'section',props.stateProps.sectionData.resumeIndex);
  };

  const chartData = Object.entries(scores)
    .sort(([, a], [, b]) => b - a)
    .map(([value, score]) => ({
      value: value,
      score: score
    }));

  const colors = {
    primary: '#252f59',
    secondary: '#dd1198',
    tertiary: '#ff9800'
  };

  if (showResults) {
    const topValues = getTopValues();
    const top5 = topValues.slice(0, 5);

    return (
      <Container maxW="container.lg" py={6}>
        <Box bg="white" p={6} borderRadius="lg" shadow="lg">
          <Heading as="h2" size="xl" textAlign="center" mb={6} color="#252f59">
            Your Core Values
          </Heading>

          <Box borderLeftWidth="4px" borderColor="#dd1198" bg="#fff5f7" p={4} mb={6} borderRadius="md">
            <Text fontSize="lg" fontWeight="semibold" color="#252f59" mb={3}>
              Your Top 5 Values:
            </Text>
            <Flex wrap="wrap" gap={2} mb={4}>
              {top5.map(([value], index) => (
                <Box
                  key={value}
                  px={3}
                  py={1}
                  borderRadius="full"
                  color="white"
                  fontWeight="semibold"
                  bg={index === 0 ? '#dd1198' : index === 1 ? '#ff9800' : '#252f59'}
                >
                  {index + 1}. {value}
                </Box>
              ))}
            </Flex>
            <Text fontSize="sm" color="gray.600">
              These values represent what matters most to you. When your work aligns with these, you feel energized and fulfilled. When it doesn't, you might feel frustrated or disconnected, even if everything else looks good on paper.
            </Text>
          </Box>

          <Box mb={8}>
            <Heading as="h3" size="lg" mb={4} color="#252f59">
              All Your Values
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="value" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={index < 2 ? '#dd1198' : index < 5 ? '#ff9800' : '#252f59'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <VStack spacing={4} align="stretch">
            <Heading as="h3" size="lg" color="#252f59">
              Understanding Your Values
            </Heading>
            {top5.map(([value, score], index) => (
              <Box
                key={value}
                borderLeftWidth="4px"
                borderColor={index === 0 ? '#dd1198' : index === 1 ? '#ff9800' : '#252f59'}
                bg="#f8f9fa"
                p={4}
                borderRadius="md"
              >
                <Heading as="h4" size="md" mb={2} color="#252f59">
                  #{index + 1}: {valueDescriptions[value].name}
                </Heading>
                <Text color="gray.700" mb={2}>
                  {valueDescriptions[value].whatItMeans}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <Text as="strong" color="#ff9800">Where you'll thrive:</Text> {valueDescriptions[value].workStyle}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <Text as="strong" color="#dd1198">Career fits:</Text> {valueDescriptions[value].careers}
                </Text>
              </Box>
            ))}
          </VStack>

          {top5.length > 1 && (() => {
            const first = top5[0][0];
            const second = top5[1][0];
            return (
              <Alert status="warning" variant="left-accent" borderRadius="lg" mt={6} bg="#fff9e6" borderColor="#ff9800" borderWidth="2px">
                <AlertIcon />
                <AlertDescription fontSize="sm" color="#252f59">
                  <Text as="strong">💡 Your Core Combination:</Text> Your top values of <Text as="strong">{first}</Text> and <Text as="strong">{second}</Text> work together to shape what fulfills you. Look for opportunities where you can honor both - that's where you'll feel most aligned and energized.
                </AlertDescription>
              </Alert>
            );
          })()}

          <Button
            onClick={() => {
              handleSaveAndContinue(props);
            }}
            w="full"
            mt={6}
            bg="#dd1198"
            color="white"
            py={6}
            fontSize="md"
            width="260px"
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Save Results & Continue
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={6}>
      <Box bg="white" p={6} borderRadius="lg" shadow="lg">
        <Heading as="h1" size="xl" textAlign="center" mb={2} color="#252f59">
          Personal Values Assessment
        </Heading>
        <Text textAlign="center" color="gray.600" mb={6}>
          Discover what truly matters to you. Think about what makes you feel fulfilled and energized at work.
        </Text>

        <Box mb={6}>
          <Flex justify="space-between" fontSize="sm" color="gray.600" mb={2}>
            <Text>Question {currentQuestion + 1} of {questions.length}</Text>
            <Text>{Math.round(((currentQuestion) / questions.length) * 100)}%</Text>
          </Flex>
          <Progress
            value={((currentQuestion) / questions.length) * 100}
            size="sm"
            colorScheme="pink"
            bg="gray.200"
            borderRadius="full"
          />
        </Box>

        <Flex
          bg="gray.50"
          p={6}
          borderRadius="lg"
          mb={6}
          minH="32"
          align="center"
          justify="center"
          direction="column"
        >
          <Text fontSize="xl" textAlign="center" fontWeight="semibold" color="#252f59" mb={2}>
            {questions[currentQuestion].text}
          </Text>
          <Text fontSize="sm" textAlign="center" fontStyle="italic" color="gray.600">
            {questions[currentQuestion].example}
          </Text>
        </Flex>

        <VStack spacing={3}>
          <Button
            onClick={() => handleAnswer(5)}
            w="full"
            bg="#ff9800"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Agree
          </Button>
          <Button
            onClick={() => handleAnswer(3)}
            w="full"
            bg="#dd1198"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Agree
          </Button>
          <Button
            onClick={() => handleAnswer(1)}
            w="full"
            bg="#252f59"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Neutral
          </Button>
          <Button
            onClick={() => handleAnswer(-1)}
            w="full"
            bg="#6c757d"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Disagree
          </Button>
          <Button
            onClick={() => handleAnswer(-3)}
            w="full"
            bg="#e9ecef"
            color="#252f59"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Disagree
          </Button>
        </VStack>
      </Box><Button
      w="full"
      mt={6}
      bg="#dd1198"
      color="white"
      py={6}
      fontSize="md"
      width="260px"
      fontWeight="semibold"
      borderRadius="lg"
      _hover={{ opacity: 0.9 }}  className="aptitudeButton"
      onClick={() => onLink(false,props.formikProps,props.stateProps,'new-aptitude')}>
      Return
    </Button>
    </Container>
  );
};

export default ValuesAssessment;